import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStopwatch, faGaugeHigh, faArrowDown, faArrowUp, faHashtag } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const StatCard = ({ label, value, sub, icon, color, bg, border }) => (
    <div
        style={{
            flex: '1 1 160px', minWidth: 150, maxWidth: 240,
            backgroundColor: '#fff',
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
        }}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </span>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color, fontSize: 13, flexShrink: 0, marginLeft: 8,
            }}>
                <FontAwesomeIcon icon={icon} />
            </div>
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
);

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px',
        textAlign: align, backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: muted ? 400 : 500, textAlign: align,
        borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const human = (d) => d?.human ?? '—';

const formatDate = (d) => {
    if (!d) return '—';
    const clean = String(d).split('T')[0];
    try {
        return new Date(clean).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return clean;
    }
};

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const DurationView = ({ duration, loading }) => {
    const { t } = useTranslation();
    if (loading && !duration) return <EmptyState message={t('assignedToChallan.emptyStates.loadingDuration')} />;
    if (!duration) return null;

    const o = duration.overall ?? {};
    const groupBy = duration.group_by ?? 'none';
    const breakdown = duration.breakdown ?? [];
    const isSupervisor = groupBy === 'supervisor';
    const isDay = groupBy === 'day';

    const cards = [
        { label: t('assignedToChallan.cards.completed'), value: fmt(o.count), sub: t('assignedToChallan.cards.subOrdersChallaned'), icon: faHashtag, color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
        { label: t('assignedToChallan.cards.average'), value: human(o.avg), sub: t('assignedToChallan.cards.meanAssignToChallan'), icon: faStopwatch, color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
        { label: t('assignedToChallan.cards.median'), value: human(o.median), sub: t('assignedToChallan.cards.outlierResistant'), icon: faGaugeHigh, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
        { label: t('assignedToChallan.cards.fastest'), value: human(o.min), sub: t('assignedToChallan.cards.shortestWindow'), icon: faArrowDown, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
        { label: t('assignedToChallan.cards.slowest'), value: human(o.max), sub: t('assignedToChallan.cards.longestWindow'), icon: faArrowUp, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
    ];

    return (
        <div>
            <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: '#189AB4' }} />
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('assignedToChallan.sections.durationOverview')}</span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{t('assignedToChallan.labels.firstAssignmentToChallan')}</span>
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {cards.map((c) => <StatCard key={c.label} {...c} />)}
                </div>
            </div>

            {(isSupervisor || isDay) && (
                <div style={cardStyle}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                            {isSupervisor ? t('assignedToChallan.sections.bySupervisor') : t('assignedToChallan.sections.dailyTrend')}
                        </div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                            {isSupervisor ? t('assignedToChallan.subtitles.avgDurationPerSupervisor') : t('assignedToChallan.subtitles.perChallanDate')}
                        </div>
                    </div>

                    {!breakdown.length && <EmptyState message={t('assignedToChallan.emptyStates.noBreakdown')} />}

                    {breakdown.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                                <thead>
                                    <tr>
                                        <Th>{isSupervisor ? t('common.analyticsColumns.supervisor') : t('common.analyticsColumns.date')}</Th>
                                        <Th align="right">{t('common.analyticsColumns.count')}</Th>
                                        <Th align="right">{t('common.analyticsColumns.average')}</Th>
                                        <Th align="right">{t('common.analyticsColumns.min')}</Th>
                                        <Th align="right">{t('common.analyticsColumns.max')}</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {breakdown.map((row, i) => (
                                        <tr
                                            key={(isSupervisor ? row.supervisor_id : row.challan_date) ?? i}
                                            style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                        >
                                            <Cell>
                                                {isSupervisor
                                                    ? (row.supervisor_name ?? t('assignedToChallan.values.unassigned'))
                                                    : formatDate(row.challan_date)}
                                            </Cell>
                                            <Cell align="right">{fmt(row.count)}</Cell>
                                            <Cell align="right"><span style={{ fontWeight: 700, color: '#7c3aed' }}>{human(row.avg)}</span></Cell>
                                            <Cell align="right" muted>{human(row.min)}</Cell>
                                            <Cell align="right" muted>{human(row.max)}</Cell>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DurationView;
